interface Point {
  x: number
  y: number
  z: number
}

interface StoreyDimension {
  x?: number | string | null
  y?: number | string | null
}

/** The Länge/Breite field that currently has focus. */
export interface FocusedDimension {
  storey: number
  axis: 'x' | 'y'
}

interface Params {
  storeys: number[]
  storeyBoundaries: StoreyBoundaries
  buildingDimensions?: StoreyDimension[]
  origin?: { x: number; y: number }
  focused?: FocusedDimension
}

/** Form fields hand back strings, so every edge coordinate is coerced before use. */
const toPositiveNumber = (value?: number | string | null): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined

  const parsed = Number(value)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

/**
 * One rectangle per storey at that storey's ceiling level.
 *
 * The two edges the focused field drives are returned separately so they can be
 * drawn highlighted - only for the storey that field belongs to, since editing
 * one storey's length leaves the others untouched.
 *
 * `buildingDimensions` is indexed by storey number, matching the seismic
 * configuration, where entry 0 is the ground floor.
 */
export const toStoreyDimensionLines = ({
  storeys,
  storeyBoundaries,
  buildingDimensions,
  origin,
  focused,
}: Params): { baseLines: [Point, Point][]; highlightedLines: [Point, Point][] } => {
  const baseLines: [Point, Point][] = []
  const highlightedLines: [Point, Point][] = []

  if (!buildingDimensions) return { baseLines, highlightedLines }

  const startX = origin?.x ?? 0
  const startY = origin?.y ?? 0

  storeys.forEach(storey => {
    const dimension = buildingDimensions[storey]
    const boundary = storeyBoundaries[storey.toString()]

    if (!dimension || !boundary) return

    const dimensionX = toPositiveNumber(dimension.x)
    const dimensionY = toPositiveNumber(dimension.y)

    if (dimensionX === undefined || dimensionY === undefined) return

    const z = boundary[1]
    const corner = (x: number, y: number): Point => ({ x, y, z })

    const bottomLeft = corner(startX, startY)
    const bottomRight = corner(startX + dimensionX, startY)
    const topRight = corner(startX + dimensionX, startY + dimensionY)
    const topLeft = corner(startX, startY + dimensionY)

    // Edges running along x carry the length, those along y the width.
    const xEdges: [Point, Point][] = [
      [bottomLeft, bottomRight],
      [topLeft, topRight],
    ]
    const yEdges: [Point, Point][] = [
      [bottomRight, topRight],
      [bottomLeft, topLeft],
    ]

    const focusedAxis = focused?.storey === storey ? focused.axis : undefined

    baseLines.push(...(focusedAxis === 'x' ? [] : xEdges))
    baseLines.push(...(focusedAxis === 'y' ? [] : yEdges))

    if (focusedAxis === 'x') highlightedLines.push(...xEdges)
    if (focusedAxis === 'y') highlightedLines.push(...yEdges)
  })

  return { baseLines, highlightedLines }
}
